import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveSiteSeverityRules } from './api'
import type { FleetSeverity, SiteSeverityRule } from './types'
import { isChosen, plural, ruleState, rulesOnSet, savePayload, siteListLabel, type TopicGroup } from './severityRules'

/**
 * Personal notification rules: "every alarm of this severity at these
 * sites", stored per site (user_site_alarm_severity_subscription), so alarm
 * types added to a severity later are included. Never touches individual
 * alarm settings or severity defaults. The parent remounts this (key) when
 * the site selection changes, which discards pending choices.
 */
export default function SubscribeBySeveritySection({
  severities,
  topicGroups,
  selectedIds,
  siteNames,
  rules,
  available,
}: {
  severities: FleetSeverity[]
  topicGroups: TopicGroup[]
  selectedIds: string[]
  siteNames: string[]
  rules: SiteSeverityRule[]
  available: boolean
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Record<number, boolean>>({})
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null)
  const onRules = useMemo(() => rulesOnSet(rules), [rules])
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const topicsBySeverity = useMemo(
    () => new Map(topicGroups.map((g) => [g.severityId, g.topics])),
    [topicGroups],
  )

  const payload = savePayload(draft, onRules, selectedIds)
  const changes = Object.entries(payload).map(([id, value]) => ({
    name: severities.find((s) => s.alarm_severity_id === Number(id))?.alarm_severity_name ?? id,
    subscribe: value === true,
  }))
  const noSites = selectedIds.length === 0
  const disabled = !available || noSites

  const saveMutation = useMutation({
    mutationFn: () => saveSiteSeverityRules(selectedIds, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(['fleet-site-severity-rules'], { available: true, rules: result.rules })
      queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] })
      const added = changes.filter((c) => c.subscribe).map((c) => c.name)
      const removed = changes.filter((c) => !c.subscribe).map((c) => c.name)
      setMessage({
        isError: false,
        text:
          `Saved for ${plural(result.systems_updated, 'site')}.` +
          (added.length ? ` Subscribed to all ${added.join(', ')} alarms.` : '') +
          (removed.length ? ` Removed ${removed.join(', ')}.` : '') +
          (result.skipped_controller_ids.length
            ? ` ${plural(result.skipped_controller_ids.length, 'site')} skipped (not accessible to you).`
            : ''),
      })
      setDraft({})
    },
    onError: (err) => {
      const apiError = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined)?.error : undefined
      setMessage({ isError: true, text: apiError || 'Failed to save severity subscriptions. Nothing was changed.' })
    },
  })

  return (
    <section aria-labelledby="subscribe-severity-heading" className="rounded-2xl border border-nav-line bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="subscribe-severity-heading" className="text-base font-semibold text-ink">
          2. Subscribe by severity
        </h2>
        <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">
          Your notifications · selected sites
        </span>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Choose the severities you want to be notified about at the selected sites. You’ll get every alarm of
        those severities there, including alarm types added to the severity later. Individual alarm settings
        still take priority.
      </p>

      {!available && (
        <p role="status" className="mt-3 rounded-2xl border border-nav-line bg-bg-tertiary p-3 text-sm text-text-secondary">
          Subscribe by severity isn’t available yet: it needs a database update that hasn’t been applied.
        </p>
      )}
      {available && noSites && (
        <p role="status" className="mt-3 rounded-2xl border border-nav-line bg-bg-tertiary p-3 text-sm text-text-secondary">
          Choose at least one site to subscribe by severity.
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {severities.map((sev) => {
          const state = ruleState(onRules, sev.alarm_severity_id, selectedIds)
          const chosen = isChosen(draft, sev.alarm_severity_id, state)
          const pending = sev.alarm_severity_id in payload
          const overridden = sev.override_systems.filter((s) => selectedSet.has(s.controller_id)).length
          const topics = topicsBySeverity.get(sev.alarm_severity_id) ?? []
          const status = noSites
            ? 'No sites selected'
            : state.coverage === 'all'
              ? `Subscribed at ${selectedIds.length === 1 ? 'the selected site' : `all ${selectedIds.length} selected sites`}`
              : state.coverage === 'some'
                ? `Subscribed at ${state.onCount} of ${selectedIds.length} selected sites`
                : 'Not subscribed at the selected sites'
          return (
            <div
              key={sev.alarm_severity_id}
              data-testid={`subscribe-severity-${sev.alarm_severity_id}`}
              className={`rounded-2xl border p-3 transition-colors ${
                chosen ? 'border-brand bg-brand/10' : state.coverage === 'some' ? 'border-dashed border-brand/60' : 'border-nav-line'
              }`}
            >
              <button
                type="button"
                aria-pressed={chosen}
                disabled={disabled || saveMutation.isPending}
                onClick={() => setDraft((d) => ({ ...d, [sev.alarm_severity_id]: !chosen }))}
                className="flex w-full items-start gap-3 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs font-bold ${
                    chosen ? 'border-brand bg-brand text-white' : 'border-nav-line bg-card'
                  }`}
                >
                  {chosen ? '✓' : ''}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">{sev.alarm_severity_name}</span>
                  <span className="block text-xs text-text-secondary">{plural(sev.topic_count, 'alarm type')}</span>
                  <span className="mt-1 block text-xs text-text-primary">{status}</span>
                  {pending && (
                    <span className="mt-1 block text-xs font-semibold text-accent">
                      {chosen ? 'Will subscribe when you save' : 'Will be removed when you save'}
                    </span>
                  )}
                </span>
              </button>
              {(sev.is_default_subscribed || overridden > 0) && (
                <ul className="mt-2 space-y-1 border-t border-nav-line pt-2 text-xs text-text-secondary">
                  {sev.is_default_subscribed && (
                    <li>Your {sev.alarm_severity_name} severity default is on, so all your sites already notify you.</li>
                  )}
                  {overridden > 0 && (
                    <li>
                      {plural(overridden, 'selected site')} {overridden === 1 ? 'has' : 'have'} individual settings for
                      some of these alarms; those take priority.
                    </li>
                  )}
                </ul>
              )}
              {topics.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer font-semibold text-accent">Review alarms ({topics.length})</summary>
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-text-primary">
                    {topics.map((t) => (
                      <li key={t.alarm_topic_id}>{t.alarm_real_name}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-nav-line pt-3">
        <p className="text-xs text-text-secondary" aria-live="polite">
          {changes.length === 0
            ? noSites
              ? 'No sites selected.'
              : `No unsaved changes for ${siteListLabel(siteNames)}.`
            : `${plural(changes.length, 'change')} for ${siteListLabel(siteNames)}: ${changes
                .map((c) => `${c.subscribe ? 'subscribe to' : 'remove'} ${c.name}`)
                .join('; ')}.`}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setDraft({})
              setMessage(null)
            }}
            disabled={changes.length === 0 || saveMutation.isPending}
            className="rounded-full border border-nav-line px-4 py-2 text-sm text-text-primary hover:bg-nav-hover disabled:opacity-50"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={changes.length === 0 || disabled || saveMutation.isPending}
            className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : `Save for ${plural(selectedIds.length, 'site')}`}
          </button>
        </div>
      </div>
      {message && (
        <p role="status" className={`mt-2 text-sm ${message.isError ? 'text-error' : 'text-brand'}`}>
          {message.text}
        </p>
      )}
    </section>
  )
}
